import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { setDefaultTimezone } from '../../helpers/formatters'
import { Note, NoteRecordType } from '../../models/note'
import {
  darkGray,
  dividerGray,
  lightGray1,
  lightGray2,
  lightgreye3,
  midGray,
  primaryBlack,
} from '../../theme/colors'
import { Divider, DividerProps } from '../Base/Divider'
import { FlexBox } from '../Base/FlexBox'
import { TextClamp } from '../Base/TextFormat'
import { useUserProvider } from '../Context/UserContext'
import { ConfirmDialog } from '../Dialog/ConfirmDialog'
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary'
import { Text } from '../Text/Text'
import { NoteComposer } from './NoteComposer'
import { NOTES_GAP } from './Notes.constants'
import { NoteText } from './NoteText'

interface NoteItemProps {
  note: Note
  recordId: string
  recordType: NoteRecordType
  isStatic?: boolean
  onDelete: (noteId: string) => Promise<void>
  onEdit: (noteId: string, text: string, tags?: string[]) => Promise<void>
}

export function NoteItem({ note, recordId, recordType, isStatic, onDelete, onEdit }: NoteItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { showBanner, loggedInUser } = useUserProvider()
  const canEdit = Boolean(loggedInUser?.id && loggedInUser?.id === note.createdBy?.id)

  function showErrorMessage(err: any) {
    showBanner('error', err)
  }

  function getNoteUser() {
    if (note.createdBy?.firstName || note.createdBy?.lastName) {
      return (
        <>
          {note.createdBy.firstName} {note.createdBy.lastName}
        </>
      )
    }

    return note.createdBy?.email.split('@')[0]
  }

  async function handleEdit(text: string, tags?: string[]) {
    await onEdit(note.id, text, tags)
    setIsEditing(false)
  }

  function renderContent() {
    if (isEditing) {
      return (
        <>
          <NoteWrapDivider $isStatic={isStatic} />
          <NoteWrapInner>
            <NoteComposer
              message={note.text}
              tags={note.tagsWithLabels}
              onMessage={handleEdit}
              onCancel={() => setIsEditing(false)}
              recordId={recordId}
              recordType={recordType}
            />
          </NoteWrapInner>
        </>
      )
    }

    return (
      <>
        <NoteWrapDivider />
        <NoteWrapInner>
          {note.tagsWithLabels.length ? (
            <Text variant="header4" color={primaryBlack} mb={1}>
              {note.tagsWithLabels.map((t, index) => (
                <span key={t.label}>
                  {index === 0 ? null : <span style={{ color: lightGray2, padding: '0 8px' }}>•</span>}
                  {t.label}
                </span>
              ))}
            </Text>
          ) : null}
          <Pre>
            <Text variant="label3" fontColor={darkGray}>
              <ErrorBoundary fallback={note.text}>
                <NoteText text={note.text} />
              </ErrorBoundary>
            </Text>
          </Pre>
          {note.dateCreated !== note.dateUpdated ? (
            <Text variant="label4" fontColor={midGray} mt={1}>
              (edited)
            </Text>
          ) : null}

          <FlexBox alignItems="center" justifyContent="space-between" mt={1} gap="5px">
            <Text variant="label3" fontColor={midGray} styles="max-width: 40%;">
              <TextClamp>{getNoteUser()}</TextClamp>
            </Text>
            <Timestamp variant="label3" fontColor={lightGray1}>
              {setDefaultTimezone(note.dateUpdated).toLocaleString({
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZoneName: 'short',
              })}
            </Timestamp>
          </FlexBox>

          {canEdit && (
            <Actions>
              <EditIcon fontSize="small" htmlColor={dividerGray} onClick={() => setIsEditing(true)} />
              <ConfirmDialog
                width="390px"
                dialogContent={
                  <Text variant="header2" align="left">
                    Delete note?
                  </Text>
                }
                confirmLabel="Delete"
                handleConfirm={() => onDelete(note.id)}
                handleError={showErrorMessage}
              >
                <DeleteIcon fontSize="small" htmlColor={dividerGray} />
              </ConfirmDialog>
            </Actions>
          )}
        </NoteWrapInner>
      </>
    )
  }

  return (
    <NoteWrap $isStatic={isStatic} $canEdit={canEdit}>
      {renderContent()}
    </NoteWrap>
  )
}

const Actions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  visibility: hidden;
  position: absolute;
  right: 0;
  bottom: 25px;

  svg {
    cursor: pointer;
  }

  svg:hover {
    color: ${lightGray2};
  }
`

const Pre = styled.div`
  margin: 0;
  padding: 0;
  white-space: pre-line;

  a {
    word-break: break-all;
    white-space: normal;
  }
`

export const Timestamp = styled(Text)``

export const NoteWrap = styled.div<{ $canEdit?: boolean; $isStatic?: boolean }>`
  position: relative;

  ${(p) =>
    p.$canEdit &&
    css`
      &:hover ${Actions} {
        visibility: visible;
      }

      &:hover ${Timestamp} {
        opacity: 0;
      }
    `}
`

export const NoteWrapInner = styled.div`
  position: relative;
  padding: 24px 0;
`

export const NoteWrapDivider = styled((props: DividerProps) => <Divider {...props} color={lightgreye3} />)<{
  $isStatic?: boolean
}>`
  position: absolute;
  left: -${NOTES_GAP}px;
  right: -${NOTES_GAP}px;

  ${(p) => p.$isStatic && `left: 0; right: 0;`}
`
